import { PrivilegeEnum } from '@/api/ApiClient';
import type { Operation } from '@/stores/authStore';

export type IsAuthorizedFn = (privilege: PrivilegeEnum | null | undefined, op: Operation) => boolean;

export type CurrentUserRef = {
  id?: string;
  faculty?: string;
};

/** List/filter visibility and transfer menu when source has no projects. */
export function canSeeUnassignedSamples(isAuthorized: IsAuthorizedFn): boolean {
  return isAuthorized(PrivilegeEnum.AccessUnassignedSample, 'read') || isAuthorized(PrivilegeEnum.Sample, 'read');
}

/** Optional project on create flows (add-to-inventory, bulk create, growth). */
export function canCreateWithoutProject(isAuthorized: IsAuthorizedFn): boolean {
  return isAuthorized(PrivilegeEnum.AccessUnassignedSample, 'create') || isAuthorized(PrivilegeEnum.Sample, 'create');
}

/** Facility triage strip-all (server also allows owner/faculty via {@link mayClearAllProjects}). */
export function canClearAllProjects(isAuthorized: IsAuthorizedFn): boolean {
  return isAuthorized(PrivilegeEnum.AccessUnassignedSample, 'update');
}

/** Sample owner or faculty account id (client approximation of server owner/faculty path). */
export function isSampleOwnerOrFacultyUser(
  sampleUserId: string | undefined | null,
  currentUser: CurrentUserRef | null | undefined
): boolean {
  if (!sampleUserId || !currentUser?.id) {
    return false;
  }
  if (sampleUserId === currentUser.id) {
    return true;
  }
  return Boolean(currentUser.faculty && sampleUserId === currentUser.faculty);
}

/** Whether empty transfer target may be offered (owner/faculty or AccessUnassignedSample/Update). */
export function mayClearAllProjects(
  isAuthorized: IsAuthorizedFn,
  sampleUserId: string | undefined | null,
  currentUser: CurrentUserRef | null | undefined
): boolean {
  return isSampleOwnerOrFacultyUser(sampleUserId, currentUser) || canClearAllProjects(isAuthorized);
}

/** Bulk strip-all UI: facility triage or every selected row is owner/faculty-owned. */
export function mayClearAllProjectsForSamples(
  isAuthorized: IsAuthorizedFn,
  samples: readonly { userId?: string | null }[],
  currentUser: CurrentUserRef | null | undefined
): boolean {
  if (canClearAllProjects(isAuthorized)) {
    return true;
  }
  if (samples.length === 0) {
    return false;
  }
  return samples.every((s) => isSampleOwnerOrFacultyUser(s.userId, currentUser));
}
